import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
} from "../@/components/ui/table";
import { MdModeEdit } from "react-icons/md";
import { FaTrashAlt } from "react-icons/fa";
import {
  useDeleteProductMutation,
  useGetProductsMutation,
  useGetProductByIdQuery,
} from "../api/api";
import SkeletonTableLoading from "../skeletonLoading/SkeletonTableLoading";
import { useToast } from "../@/components/ui/use-toast";
import { useSelector } from "react-redux";
import useDebounce, {
  formatDate,
  formatDateTime,
  IsServiceTypeVendor,
  tokenDecoder,
} from "../lib/utils";
import { useNavigate } from "react-router-dom";
import { MoveUp, MoveDown, ArrowUpDown } from "lucide-react";
import { Input } from "../@/components/ui/input";
import { FiSearch } from "react-icons/fi";
import { setAppState } from "../redux/slices/appState";
import { useDispatch } from "react-redux";
import SingleDatePicker from "./SingleDatePicker";
import Pagination from "./Pagination";
import ShareButton from "./ShareButton";
import { DEEP_LINK_BASE_URL } from "../lib/constants";
import { IoMdShare } from "react-icons/io";
import { IData } from "../lib/interfaces";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends unknown, TValue> {
    isTooltip?: boolean;
  }
}

interface Props {
  subCategoryId?: number;
  categoryId?: number;
  setSelectedSubCategoryId?: any;
  setSelectedCategoryId?: any;
  setOpenAccordion?: any;
  openAccordion: any;
}

const ProductsListView: React.FC<Props> = ({
  subCategoryId,
  categoryId,
  setSelectedSubCategoryId,
  setSelectedCategoryId,
  setOpenAccordion,
  openAccordion,
}) => {
  const allLabel = IsServiceTypeVendor() ? "All Service" : "All Product";

  const [selected, setSelected] = useState<any>({
    name: allLabel,
    id: undefined,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const vendorId = decodedToken.vendorId!;
  const isMobile = window.innerWidth < 768;

  const options: {
    name: string;
    id: number | undefined;
    boolean: boolean | undefined;
  }[] = [
    { name: allLabel, id: undefined, boolean: undefined },
    { name: "Published", id: 12, boolean: undefined },
    { name: "Draft", id: 11, boolean: undefined },
    ...(IsServiceTypeVendor()
      ? []
      : [{ name: "Low Stock", id: undefined, boolean: true }]),
  ];

  const [data, setData] = useState<IData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, _] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deletingProductId, setDeletingProductId] = useState<number | null>(
    null
  );
  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc" | undefined>(
    "desc"
  );
  const [sortingColumn, setSortingColumn] = useState<string | undefined>(
    "createdAt"
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(0);

  const [getProducts, { error, isLoading: productsLoading }] =
    useGetProductsMutation();
  const [deleteProduct, { isLoading: isDeleting, error: deleteError }] =
    useDeleteProductMutation();
  const dispatch = useDispatch();

  const handleSorting = (column: string) => {
    if (sortingColumn === column) {
      setSortingOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortingColumn(column);
      setSortingOrder("asc");
    }
    setPageNumber(1);
  };

  const fetchData = useCallback(async () => {
    try {
      const response: any = await getProducts({
        getProductReqDto: {
          vendorId: vendorId,
          searchQuery:
            debouncedSearchQuery !== "" ? debouncedSearchQuery : undefined,
          pageNumber,
          pageSize,
          getTotalCount: true,
          statusId: selected.id,
          subCategoryId: subCategoryId !== -1 ? subCategoryId : undefined,
          categoryId: categoryId !== -1 ? categoryId : undefined,
          isLowStock: selected.boolean,
          sortingOrder,
          sortingColumn,
          startDate: startDate
            ? formatDateTime(startDate, "YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          endDate: endDate
            ? formatDateTime(
                endDate.setHours(23, 59, 59, 999),
                "YYYY-MM-DDTHH:mm:ssZ"
              )
            : undefined,
        },
      }).unwrap();
      setData(response?.data?.products || []);
      setTotalCount(response?.data?.totalCount || 0);
    } catch (err) {
      console.error("Failed to get products: ", err);
    }
  }, [
    getProducts,
    debouncedSearchQuery,
    selected,
    pageNumber,
    subCategoryId,
    categoryId,
    vendorId,
    sortingOrder,
    sortingColumn,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, pageNumber]);

  const handleSearchChange = () => {
    if (openAccordion !== "") {
      setOpenAccordion("");
      setSelectedCategoryId(-1);
      setSelectedSubCategoryId(-1);
    }
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
  };

  const handlePageChange = (newPageNumber: number) => {
    setOpenAccordion("");
    setSelectedCategoryId(-1);
    setSelectedSubCategoryId(-1);
    if (newPageNumber < 0 || newPageNumber > Math.ceil(totalCount / pageSize)) {
      return;
    }
    setPageNumber(newPageNumber);
  };

  const handleRemoveProduct = async (productId: number) => {
    setDeletingProductId(productId);
    try {
      await deleteProduct({ deleteProductDto: { id: productId } }).unwrap();
      toast({
        title: "Success",
        description: "Product deleted successfully",
      });
      setDeletingProductId(null);
      fetchData();
    } catch (err: any) {
      console.error("Failed to delete the product: ", err?.data?.message);
      toast({
        title: "Error",
        description: `${err?.data?.message}`,
      });
      setDeletingProductId(null);
    }
  };

  const handleShareProduct = (productId: number) => {
    setIsDialogOpen(true);
    setSelectedProductId(productId);
  };
  const isServiceTypeVendor = IsServiceTypeVendor();

  const handleEditProduct = (productId: number) => {
    const routeName = isServiceTypeVendor ? "edit-service" : "edit-product";
    navigate(`/${routeName}/${productId}`);
  };

  const columns = useMemo<ColumnDef<IData>[]>(() => {
    const baseColumns: ColumnDef<IData>[] = [
      {
        accessorKey: "productName",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("productName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            {isServiceTypeVendor ? "Service" : "Product"}{" "}
            {sortingColumn === "productName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 200,
        cell: ({ row }) => (
          <div className="flex items-center break-words">
            <img
              src={row.original.defaultImageUrl}
              alt={row.original.productName}
              onError={(e) => {
                e.currentTarget.src = "/logo192.png";
              }}
              className="w-10 h-10 mr-2 rounded-lg border object-fill"
            />
            <span className="capitalize overflow-hidden text-ellipsis">
              {row.original.productName.length > 30
                ? `${row.original.productName.substring(0, 30)}...`
                : row.original.productName}
            </span>
          </div>
        ),
      },
      ...(!isServiceTypeVendor
        ? [
            {
              accessorKey: "sku",
              header: ({ column }: { column: any }) => (
                <div
                  onClick={() => handleSorting("sku")}
                  className="cursor-pointer flex gap-1 items-center"
                >
                  SKU{" "}
                  {sortingColumn === "sku" ? (
                    sortingOrder === "asc" ? (
                      <MoveUp size={14} />
                    ) : (
                      <MoveDown size={14} />
                    )
                  ) : (
                    <ArrowUpDown size={14} />
                  )}
                </div>
              ),
              size: 150,
              cell: ({ cell }: { cell: any }) => (
                <span className="overflow-hidden text-ellipsis">
                  {cell.getValue() as string}
                </span>
              ),
            },
          ]
        : []),
      {
        accessorKey: "category",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("category")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Category{" "}
            {sortingColumn === "category" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 150,
      },
      {
        accessorKey: "subCategory",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("subCategory")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Sub Category{" "}
            {sortingColumn === "subCategory" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 150,
      },
      ...(!isServiceTypeVendor
        ? [
            {
              accessorKey: "quantityAvailable",
              header: ({ column }: { column: any }) => (
                <div
                  onClick={() => handleSorting("quantityAvailable")}
                  className="cursor-pointer flex gap-1 items-center"
                >
                  Stock{" "}
                  {sortingColumn === "quantityAvailable" ? (
                    sortingOrder === "asc" ? (
                      <MoveUp size={14} />
                    ) : (
                      <MoveDown size={14} />
                    )
                  ) : (
                    <ArrowUpDown size={14} />
                  )}
                </div>
              ),
              cell: ({ cell }: { cell: any }) => (
                <span>{cell.getValue() as number}</span>
              ),
              size: 100,
            },
          ]
        : []),
      {
        accessorKey: "price",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("price")}
            className="cursor-pointer flex gap-1 items-center text-nowrap"
          >
            Price{" "}
            {sortingColumn === "price" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {row?.original?.oldPrice !== null
                ? "$" + row?.original?.oldPrice?.toFixed(2)
                : "$" + row.original.price.toFixed(2)}
            </span>
          );
        },
        size: 100,
      },
      {
        accessorKey: "status",
        header: ({ column }) => (
          <div
            onClick={() => {
              if (!isServiceTypeVendor) {
                handleSorting("status");
              }
            }}
            className={`cursor-pointer flex gap-1 justify-center items-center ${
              isServiceTypeVendor ? "pointer-events-none" : ""
            }`}
          >
            Status{" "}
            {sortingColumn === "status" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        meta: {
          isTooltip: false,
        },
        cell: ({ row }) => {
          const quantityAvailable = row.original.quantityAvailable;
          const status = isServiceTypeVendor
            ? row.original.status === "Activated"
              ? "Published"
              : row.original.status
            : quantityAvailable < 10
            ? "Low Stock"
            : row.original.status === "Activated"
            ? "Published"
            : row.original.status;

          return (
            <div className="flex items-center justify-center">
              <span
                className={`p-2 py-1 rounded-md text-xs ${
                  status === "Drafted"
                    ? "bg-slate-200 text-black"
                    : status === "Published"
                    ? "bg-customGreenLight text-green-700"
                    : status === "Low Stock"
                    ? "bg-cancelledStatusBgColor text-cancelledStatusTextColor"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {status}
              </span>
            </div>
          );
        },
        size: 120,
        enableSorting: !isServiceTypeVendor, // Disable sorting when serviceTypeVendorTrue is true
        filterFn: isServiceTypeVendor ? "equals" : undefined, // Disable filtering when serviceTypeVendorTrue is true
      },

      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("createdAt")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Added On{" "}
            {sortingColumn === "createdAt" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        cell: ({ cell }) => (
          <span>{formatDate(cell.getValue() as string, "dd MMM yyyy")}</span>
        ),
        meta: {
          isTooltip: false,
        },
        size: 120,
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                dispatch(setAppState({ routeName: "edit Product" }));
                handleEditProduct(row.original.productId);
              }}
            >
              <MdModeEdit />
            </button>
            <button onClick={() => handleShareProduct(row.original.productId)}>
              <IoMdShare className="text-black" />
            </button>
            <button
              onClick={() => handleRemoveProduct(row.original.productId)}
              disabled={deletingProductId === row.original.productId}
            >
              {deletingProductId === row.original.productId ? (
                <div className="loader" />
              ) : (
                <FaTrashAlt />
              )}
            </button>
          </div>
        ),
        meta: {
          isTooltip: false,
        },
        size: 100,
      },
    ];

    return baseColumns;
  }, [sortingColumn, sortingOrder, deletingProductId, isServiceTypeVendor]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalCount / pageSize),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
    },
  });

  return (
    <div className="overflow-hidden pr-2 h-full">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="flex justify-between bg-white rounded-lg border p-[1px] border-gray-300 w-full md:w-auto">
          {options.map((option) => (
            <button
              key={option.name}
              onClick={() => {
                setOpenAccordion("");
                setSelectedCategoryId(-1);
                setSelectedSubCategoryId(-1);
                setPageNumber(1);
                setSelected(option);
              }}
              className={`flex-1 w-[4.5rem] p-2 text-center text-[10px] ${
                selected.name === option.name
                  ? "bg-customGreenLight text-customGreenColor font-base"
                  : "text-gray-500 hover:bg-gray-100"
              } rounded-lg ${option.name === allLabel ? "rounded-l-lg" : ""} ${
                option.name === "Draft" ? "rounded-r-lg" : ""
              }`}
            >
              {option.name}
            </button>
          ))}
        </div>
        <div className="flex flex-col md:flex-row justify-end gap-2 w-full md:w-auto">
          <div className="relative flex justify-center text-customGrayText items-center w-full md:w-48">
            <FiSearch className="absolute left-2 text-lg" />
            <Input
              placeholder={
                IsServiceTypeVendor() ? "Search service" : "Search product"
              }
              className="text-sm text-left h-9 pl-8"
              onChange={(e: any) => {
                setSearchTerm(e.target.value);
                handleSearchChange();
              }}
            />
          </div>
          <SingleDatePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateSelect={setStartDate}
            onEndDateSelect={setEndDate}
          />
        </div>
      </div>
      {productsLoading ? (
        <div className="overflow-y-auto h-[calc(100vh-12rem)]">
          <div className="w-full rounded-lg shadow bg-white h-full">
            <Table>
              <TableHeader className="bg-customGreenColor text-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        className={`font-medium text-sm text-nowrap`}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
            <SkeletonTableLoading />
          </div>
        </div>
      ) : error ? (
        <div>
          Error loading {IsServiceTypeVendor() ? "services" : "products"}.
        </div>
      ) : (
        <>
          <div className="overflow-y-auto h-[calc(100vh-12rem)]">
            <div className="overflow-x-auto w-full rounded-lg shadow bg-white h-full">
              <Table>
                <TableHeader className="bg-customGreenColor text-white sticky top-0">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={`font-medium text-sm text-nowrap`}
                          style={{
                            minWidth: header.column.columnDef.size,
                            maxWidth: header.column.columnDef.size,
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="text-center"
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={`font-medium text-sm whitespace-nowrap overflow-hidden`}
                            style={{
                              minWidth: cell.column.columnDef.size,
                              maxWidth: cell.column.columnDef.size,
                            }}
                            isTooltip={
                              cell.column.columnDef.meta?.isTooltip ?? true
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          {data.length > 0 && (
            <div
              className={`flex justify-between items-center mt-2 w-auto ${
                isMobile ? "flex-col" : "flex-row"
              }`}
            >
              <div className="text-[#667085]">
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {Math.ceil(totalCount / pageSize)}
              </div>
              <Pagination
                totalCount={totalCount}
                pageSize={pageSize}
                currentPage={pageNumber}
                onPageChange={handlePageChange}
              />
            </div>
          )}
          <ShareButton
            setIsDialogOpen={setIsDialogOpen}
            isDialogOpen={isDialogOpen}
            title={`Share Your ${
              isServiceTypeVendor ? "Service" : "Product"
            } Link`}
            text={`Share your ${
              isServiceTypeVendor ? "service" : "product"
            } with friends and customers! Copy the link below and send it to them via email, chat, or social media to showcase your product and grow your business.`}
            url={`${DEEP_LINK_BASE_URL}/Main/HomeStack/ProductDetailContainer?id=${selectedProductId}`}
          />
        </>
      )}
    </div>
  );
};

export default ProductsListView;
